/**
 * English source dictionary.
 *
 * This file is the single source of truth for product copy. Every other locale
 * is typed against `Messages`, so a missing or misspelled key fails the build
 * rather than shipping an untranslated string.
 *
 * Conventions:
 *   * Keys describe *where* and *what*, not the English wording, so copy can be
 *     reworded without touching code.
 *   * `{name}` marks an interpolation slot.
 *   * A leaf may be `{ one, other }` for a count-sensitive string. The variant is
 *     chosen with `Intl.PluralRules`, so a locale with more than two plural
 *     categories can add them without a code change.
 *   * Never build a sentence by concatenating translated fragments — word order
 *     differs. Put the whole sentence in one value with slots.
 */

export const en = {
  common: {
    appName: 'Yummy',
    save: 'Save',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    close: 'Close',
      pickRangeStart: 'Pick the first day.',
      pickRangeEnd: 'Now pick the last day.',
    delete: 'Delete',
    remove: 'Remove',
    edit: 'Edit',
    add: 'Add',
    back: 'Back',
    next: 'Next',
    retry: 'Try again',
    justAMoment: 'Just a moment.',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    loading: 'Loading…',
    somethingWentWrong: 'Something went wrong',
    notFound: 'Page not found',
    optional: 'Optional',
    required: 'Required',
    today: 'Today',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    tomorrow: 'Tomorrow',
    yesterday: 'Yesterday',
    closed: 'Closed',
    open: 'Open',
    openNow: 'Open',
    // Labels for the lines that leave the app, so a screen reader says where a
    // tap goes rather than reading out an address and a number as bare text.
    openInMaps: 'Open {address} in Google Maps',
    callRestaurant: 'Call {phone}',
    search: 'Search',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    of: 'of',
    and: 'and',
    or: 'or',
    done: 'Done',
    confirm: 'Confirm',
    dismiss: 'Dismiss',
    copy: 'Copy',
    copied: 'Copied',
    print: 'Print',
    keep: 'Keep',
    show: 'Show',
    hide: 'Hide',
    name: 'Name',
    description: 'Description',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    note: 'Note',
    notes: 'Notes',
    price: 'Price',
    quantity: 'Quantity',
    total: 'Total',
    subtotal: 'Subtotal',
    discount: 'Discount',
    tax: 'Tax',
    currency: 'Currency',
    password: 'Password',
    country: 'Country',
    timezone: 'Timezone',
    date: 'Date',
    time: 'Time',
    hour: 'Hour',
    minute: 'Minute',
    status: 'Status',
    details: 'Details',
    actions: 'Actions',
    settings: 'Settings',
    languageAndAppearance: 'Language & appearance',
    language: 'Language',
    appearance: 'Appearance',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    themeSystemHint: 'Follow the device setting',
    localeSystem: 'Follow device language',
    localeSystemShort: 'Follow device',
    switchLanguage: 'Switch language',
    switchToDark: 'Switch to dark theme',
    switchToLight: 'Switch to light theme',
    overnightSuffix: ' (+1 day)',
    itemCount: { one: '{count} item', other: '{count} items' },
    orderCount: { one: '{count} order', other: '{count} orders' },
  },

  /** Phrasing for countdowns, which are deliberately coarse. */
  time: {
    ready: 'Ready',
    anyMoment: 'any moment now',
    underAMinute: 'under a minute',
    aboutMinutes: 'about {count} min',
    aboutHours: 'about {hours} hr',
    aboutHoursMinutes: 'about {hours} hr {minutes} min',
  },

  units: {
    secondShort: 's',
    minuteShort: 'm',
    hourShort: 'h',
    /** Separator between a value and its unit when the unit follows the number. */
    valueUnit: '{value}{unit}',
    /** Two-unit durations, for example `12m 30s`. */
    pair: '{first} {second}',
    /** Chinese-style unit suffixes; English leaves these as the short forms. */
    secondsOnly: '{value}s',
    minutesOnly: '{value}m',
    minutesSeconds: '{minutes}m {seconds}s',
    hoursOnly: '{value}h',
    hoursMinutes: '{hours}h {minutes}m',
    notMeasured: '—',
  },

  /** Relative phrasing that needs a translated wrapper around a duration. */
  relative: {
    ago: '{time} ago',
    inFuture: 'in {time}',
  },

  /** Order lifecycle labels. Keys match the `order_status` enum. */
  orderStatus: {
    pending: 'New',
    accepted: 'Accepted',
    preparing: 'Preparing',
    ready: 'Ready for pickup',
    completed: 'Collected',
    cancelled: 'Cancelled',
  },

  customer: {
    landing: {
      tagline: 'Order ahead, skip the queue',
      intro:
        'Scan the QR code at the restaurant, order from your phone, and collect at the counter when it is ready.',
      howItWorks: 'How it works',
      step1: 'Scan the QR code on your table or at the counter.',
      step2: 'Choose what you want and when you want to collect it.',
      step3: 'Leave a mobile number so we can reach you.',
      step4: 'Show your order code at the counter, or let us scan it.',
      haveOrder: 'Already have an order?',
      haveOrderHint:
        'Enter the six-character code from your receipt or the counter screen.',
      orderCode: 'Order code',
      findOrder: 'Find my order',
      openedByMistake: 'Opened this by mistake?',
      openedByMistakeHint:
        "Type the restaurant's web address, or paste the link from another QR code.",
      restaurant: 'Restaurant',
      openMenu: 'Open menu',
      pickupOnly: 'Pickup only',
      pickupOnlyHint:
        'Yummy restaurants do not deliver. Everything is collected in person, which is what keeps it quick.',
      previousOrders: 'My previous orders',
    },

    menu: {
      chooseGroup: 'Choose {group}',
      done: 'Done',
      change: 'Change',
      changeChoices: 'Change the choices',
      minimumOrder: 'Minimum {count}',
      orderingFrom: 'Ordering from {table}',
      loading: 'Loading the menu…',
      sessionFailed: 'We could not start your session',
      sessionStarting: 'Getting things ready',
      other: 'Other',
      notTakingOrders: 'Not taking orders',
      notTakingOrdersHint: 'This restaurant has paused online ordering. Please ask at the counter.',
      closedNow: 'Closed right now',
      closedScheduleHint: 'You can still schedule a pickup for {when}.',
      closedCheckBack: 'Please check back during opening hours.',
      pickupOnlyHours: 'Pickup only · {hours}',
      gone: 'That restaurant is no longer available.',
      myOrders: 'My orders',
      empty: 'Nothing on the menu yet',
      emptyHint: 'This restaurant has not published any items. Please ask at the counter.',
      soldOut: 'Sold out',
      // The dish sheet.
      addItem: 'Add {item}',
      addToOrder: 'Add to order',
      basePrice: 'base price',
      breakdownBase: '{total} base',
      breakdownExtras: '{count} extras {total}',
      chooseOne: 'Choose 1',
      pickUpTo: 'Pick up to {count}',
      required: 'Required',
      optional: 'Optional',
      included: 'Included',
      onlyLeft: 'Only {count} left',
      availableFrom: 'Available {day} {from}–{to}',
      unavailableNow: 'Unavailable now',
      pricesNote: 'Pickup only. Prices in {currency}{taxNote}.',
      taxNote: ', excluding {rate}% tax',
      offerEnds: 'Offer ends {when}',
      onOffer: 'On offer',
      scheduleHint: 'You can schedule a pickup time at checkout',
      loadFailed: 'We could not load this menu',
      loadFailedHint: 'The menu is unavailable.',
      switchRestaurant:
        'Your order contains items from another restaurant. Start a new order here?',
    },

    verified: {
      mark: 'Verified restaurant — what this means',
      title: 'Verified restaurant',
      reason: "Yummy has checked this restaurant's name, address and contact details.",
      since: 'Verified since {when}.',
    },

    cart: {
      review: 'Review order',
    },

    checkout: {
      screenTitle: 'Checkout',
      myOrders: 'My orders',
      phoneShort: 'Short for {dial} — local numbers are {min} digits.',
      phoneLong: 'Long for {dial} — local numbers are up to {max} digits.',
      phoneForeign: 'This looks like a {dial} number. The restaurant may need to call internationally.',
      elsewhereTitle: 'This order is for pickup in {country}',
      elsewhere: 'You appear to be in {country}. Orders are collected from the restaurant — can you make it?',
      title: 'Your order',
      loading: 'Loading checkout…',
      unavailable: 'Checkout is unavailable',
      pickupTime: 'Pickup time',
      asap: 'As soon as possible',
      asapHint: 'Ready when it is ready',
      asapClosed: 'Currently closed',
      schedule: 'Schedule',
      scheduleHint: 'Choose a time',
      estimatedReady: 'Estimated ready {when}',
      estimateInitial: ' (initial estimate)',
      estimateLearned: ' (from {count} similar past orders)',
      chooseTime: 'Choose a pickup time',
      yourDetails: 'Your details',
      yourDetailsHint: 'We only use these to prepare and hand over your order.',
      nameLabel: 'Name',
      namePlaceholder: 'Who is collecting?',
      phoneLabel: 'Mobile number',
      phonePlaceholder: '+65 9123 4567',
      phoneHint: 'Required so the restaurant can reach you about this order.',
      emailLabel: 'Email',
      emailPlaceholder: 'you@example.com',
      emailHint: 'Optional. Add it and we will email your receipt.',
      noteLabel: 'Notes for the kitchen',
      notePlaceholder: 'Allergies, less spicy, no cutlery…',
      payment: 'Payment',
      payAtCounter: 'Pay at the counter',
      payAtCounterHint:
        'This order is not paid online. Show your order code when you collect.',
      placeOrder: 'Place order',
      placing: 'Placing…',
      orderPlacedWithReceipt: 'Order {number} placed — a receipt is on its way to {email}',
      orderPlaced: 'Order {number} placed',
      noSlots: 'No pickup times available',
      noSlotsHint:
        'This restaurant is fully booked or closed for the next week. Try ordering as soon as possible instead.',
      slotTimesAvailable: '{count} times available',
      findingSlots: 'Finding pickup times…',
      slotTooSoonChip: '{time}, very soon for this order',
      slotTooSoonKey: 'Very soon for this order',
      slotTooSoonTitle: 'This pickup time is very soon',
      slotTooSoonMessage:
        'Your order usually takes about {minutes} min, so the kitchen may not have it ready by then.',
      slotTooSoonAction: 'Use {time} instead',
    },

    order: {
      screenTitle: 'Order',
      loading: 'Loading your order…',
      loadFailed: 'We could not load this order',
      loadFailedHint: 'It may have been removed.',
      notFound: 'Order not found',
      notFoundByCode: 'We could not find an order with the code {code}.',
      goBack: 'Go back',
      scheduled: 'Scheduled pickup',
      pending: 'Waiting for the restaurant',
      accepted: 'Accepted by restaurant',
      preparing: 'Being prepared',
      readyTitle: 'Ready for pickup',
      collected: 'Collected — enjoy!',
      cancelled: 'Order cancelled',
      pickupTime: 'Pickup time',
      pickupTimeHint: 'We will have it ready for you then.',
      estimatedReady: 'Estimated ready',
      readyNow: 'Ready for pickup',
      anyMomentNow: 'Any moment now',
      lateBy: '{when} late',
      // Said once the kitchen is behind but before the promise is broken — the
      // one moment a customer can still decide to wait or call. Forward-looking
      // because at that point nothing is late yet: for a booked order this can
      // arrive while the pickup time is still minutes away.
      runningBehind: "We're running a little behind with your order.",
      runningLate: 'Running late.',
      notAccepted: 'This order has not been accepted yet.',
      // A scheduled order the customer knowingly booked tight, so the same
      // warning is worded as the second half of the checkout's own sentence
      // rather than as something the kitchen has dropped.
      notAcceptedSoon: 'This pickup time is very soon. The kitchen has not accepted it yet.',
      callForUpdate: 'Call the restaurant for an update',
      comeToCounter: 'Come to the counter',
      comeToCounterHint: 'Show the QR code below, or tell us your order code.',
      showWhenCollecting: 'Show this when you collect',
      showWhenCollectingHint: 'We will call this number when it is ready.',
      orderNumber: 'Order #{number}',
      codeLabel: 'Code {code} — for staff to type in',
      summaryLine: {
        one: '{count} item',
        other: '{count} items',
      },
      discountApplied: '{label} applied',
      // The money, said only when the counter has taken it. `paid_at` is a fact
      // beside the status rather than a stage of it, so this rides with the
      // status chip instead of moving the headline — and `Unpaid` is deliberately
      // silent on this side: the customer pays at the counter, so a chip reading
      // "Unpaid" beside an order the kitchen has not reached yet would look like
      // something wrong with their order rather than a till nobody has touched.
      paid: 'Paid',
      cancelReasonCustomer: 'Cancelled by customer',
      stepPlaced: 'Order received',
      stepAccepted: 'Accepted by restaurant',
      stepPreparing: 'Being prepared',
      stepReady: 'Ready for pickup',
      stepCompleted: 'Collected',
      stepCancelled: 'Cancelled',
      whatYouOrdered: 'What you ordered',
      yourNote: 'Your note',
      pickupFrom: 'Pickup from',
      receiptSent: 'A receipt was sent to {email}.',
      backToMenu: 'Back to menu',
      myOrders: 'My orders',
      cancelOrder: 'Cancel order',
      collectedIt: 'I have collected it',
      cancelTitle: 'Cancel this order?',
      cancelMessage:
        'The restaurant has not started preparing it yet. This cannot be undone.',
      cancelConfirm: 'Cancel order',
      cancelKeep: 'Keep order',
      cancelledToast: 'Order cancelled',
      readyToast: 'Your order is ready for pickup!',
      collectedToast: 'Collected — enjoy your meal.',
      cancelledByStaffToast: 'This order was cancelled.',
    },

    history: {
      title: 'My orders',
      loading: 'Loading your orders…',
      empty: 'No orders yet',
      emptyHint: 'Scan a QR code at a restaurant to place your first order.',
      enterCode: 'Enter a code',
      forScheduled: 'For {when}',
      placedAt: 'Placed {when}',
    },
  },

  merchant: {
    pickup: {
      // Only the arrival states live here now: a code that resolves opens on the
      // board, so the order's own words are the board's, not this route's.
      loading: 'Looking up the order…',
      notFoundTitle: 'No order for that code',
      notFound: 'The code may be from another restaurant, or the order may be finished.',
    },
    nav: {
      dashboard: 'Dashboard',
      orders: 'Orders',
      cook: 'To cook',
      menu: 'Menu',
      hours: 'Hours',
      capacity: 'Capacity',
      tables: 'Tables',
      settings: 'Settings',
      merchant: 'Merchant',
      restaurant: 'Restaurant',
      signOut: 'Sign out',
      collapse: 'Collapse sidebar',
      expand: 'Expand sidebar',
    },

    connection: {
      live: 'Live',
      connecting: 'Connecting',
      offline: 'Reconnecting',
      newCount: '· {count} new',
    },

    auth: {
      signInTitle: 'Merchant sign in',
      signUpTitle: 'Create your restaurant account',
      signInIntro: 'Manage orders, your menu, and today’s takings.',
      signUpIntro: 'Set up your menu, opening hours, and start taking pickup orders.',
      emailPlaceholder: 'owner@restaurant.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'At least 8 characters',
      passwordHint: 'At least 8 characters.',
      signIn: 'Sign in',
      signUp: 'Create account',
      haveAccount: 'I already have an account',
      needAccount: 'Create a new account',
      checkInbox: 'Check your inbox',
      continueWithGoogle: 'Continue with Google',
      orDivider: 'or',
      googleUnavailable: 'Google sign-in is not available for this restaurant yet.',
      confirmationSent: 'We sent a confirmation link to {email}. Confirm it, then sign in.',
      terms: 'By continuing you agree to use Yummy only for lawful restaurant ordering.',
      loading: 'Checking your session.',
      loadingTitle: 'Loading',
    },

    onboarding: {
      title: 'Set up your restaurant',
      intro:
        'You can change any of this later. Opening hours default to 9am–9pm every day.',
      basics: 'The basics',
      restaurantName: 'Restaurant name',
      restaurantNamePlaceholder: 'My Restaurant',
      webAddress: 'Web address',
      webAddressPlaceholder: 'my-restaurant',
      webAddressHint:
        'Used in your QR code and links. Lowercase letters, numbers, and hyphens.',
      descriptionPlaceholder: 'What should customers know before they order?',
      locationAndMoney: 'Location and money',
      locationAndMoneyHint:
        'Opening hours and daily sales totals are measured in this timezone.',
      currencyLabel: 'Currency',
      addressHint: 'Shown to customers on their order so they know where to collect.',
      pickupOnly: 'Pickup only',
      pickupOnlyHint:
        'Yummy does not handle delivery. Customers order ahead and collect from your counter.',
      create: 'Create restaurant',
      noRestaurant: 'No restaurant selected',
      noRestaurantHint: 'Create a restaurant to see its dashboard.',
    },

    dashboard: {
      title: 'Dashboard',
      netSales: 'Net sales',
      netSalesHint: '{gross} gross, {discount} discounted',
      orders: 'Orders',
      ordersHint: '{completed} collected · {open} in progress',
      averageOrder: 'Average order',
      averageOrderHint: 'Across {count} collected orders',
      onTimeRate: 'On-time rate',
      onTimeHint: '{onTime} of {measured} beaten the estimate',
      onTimeNone: 'No completed orders to measure yet',
      typicalWait: 'Typical wait',
      typicalWaitHint: '9 in 10 under {duration}',
      cancellations: 'Cancellations',
      cancellationsHint: '{cancelled} of {placed} orders',
      fulfilment: 'Fulfilment efficiency',
      fulfilmentHint:
        'Where the time in each order actually goes, from the moment it is placed to the moment it is handed over.',
      stageSitting: 'Sitting unseen',
      stageSittingHint: 'Placed → accepted. Long here means orders are not being noticed.',
      stageKitchen: 'Kitchen',
      stageKitchenHint: 'Accepted → ready. This is the time the kitchen controls.',
      stageHandover: 'Waiting on the counter',
      stageHandoverHint: 'Ready → collected. Long here means customers are not being told.',
      estimateAccuracy: 'Estimate accuracy',
      estimateAccuracyHint:
        'Average gap between the estimate quoted at checkout and when the order was actually ready. Lower is better.',
      completedRate: 'Completed',
      whereOrdersCame: 'Where the orders came in',
      whereOrdersCameHint: 'Orders per hour of the trading day.',
      scheduled: 'Booked',
      walkUp: 'Walk-up',
      scheduledShare: 'booked',
      scheduledVsWalkupHint: 'Booked ahead, or at the counter.',
      sales: 'Sales',
      salesHint: 'Net sales collected each day, in {currency}.',
      salesHourHint: 'Net sales collected each hour of the trading day, in {currency}.',
      salesMonthHint: 'Net sales collected each month, in {currency}.',
      salesPeak: 'Peak',
      bestSellers: 'Best sellers',
      bestSellersHint: 'By quantity collected in this period.',
      etaLearning: 'Estimate learning',
      etaLearningHint: 'What your own orders say about prep times.',
      medianPrep: 'Median prep time',
      averagePrep: 'Average prep',
      learnedFrom: 'orders learned from, over {days} days',
      slowestDishes: 'Slowest dishes',
      biasLong: 'Estimates run {duration} long.',
      biasShort: 'Estimates run {duration} short.',
      biasWithin: 'Estimates are within a minute.',
      lastLearned: 'Last learned from an order {when}.',
      slowestTen: 'Slowest 10%',
      stillLearningHint:
        'Until about ten orders are completed, estimates are based on the prep times you set per dish. {count} so far.',
      noHistory: 'No estimate history yet.',
      emptyPeriod: 'No orders in this period',
      emptyPeriodHint:
        'Once customers start ordering, this page fills in automatically. Try a wider range such as Last 7 days.',
      rangeToday: 'Today',
      rangeYesterday: 'Yesterday',
      rangeWeek: 'Last 7 days',
      rangeMonth: 'This month',
      rangeLastMonth: 'Last month',
      rangeYear: 'This year',
      rangeLast28Days: 'Last 28 days',
      period: 'Reporting period',
    },

    cook: {
      title: 'To cook',
      emptyTitle: 'Nothing to cook',
      emptyHint: 'Dishes appear here as orders arrive, and leave as they are marked ready.',
      // ---- the two modes ---------------------------------------------------
      // One queue read two ways: by order because the order is what is handed over,
      // by batch because a wok holds several at once. Both read the ready-by.
      byOrder: 'By order',
      byBatch: 'By batch',
      orderSummary: { one: '{count} order', other: '{count} orders' },
      roundSummary: { one: '{count} round', other: '{count} rounds' },
      batchSummary: { one: '{count} batch', other: '{count} batches' },
      bandLater: 'Later',
      bandLateRule: 'the ready-by has gone',
      bandNowRule: 'ready within ten minutes',
      readyByLabel: 'ready by',
      readyBy: 'ready by {time}',
      readyNow: 'ready now',
      inMinutes: { one: 'in {count} min', other: 'in {count} min' },
      lateBy: { one: '{count} min late', other: '{count} min late' },
      startBy: 'Start by {time}',
      // The ticket's booked line: the two facts its chips used to carry, as one
      // sentence. One key rather than the chips' two, because the clauses swap
      // order between languages — Chinese puts the time first.
      scheduledStartBy: 'Scheduled · start by {time}',
      addedCount: { one: '+{count} added', other: '+{count} added' },
      lateAccepted: { one: '+{count} accepted late', other: '+{count} accepted late' },
      roundLateAccepted: { one: '{count} accepted late', other: '{count} accepted late' },
      roundReadyAt: 'Ready by {time}',
      roundReadyNow: 'Ready now · {time}',
      // ---- ticking a round off ---------------------------------------------
      // A round is a view of orders, so finishing it is the order's own
      // transition written once per ticket: the food is made, the customer is
      // told to collect. The count is in the label because it is the blast
      // radius of one tap, and the dialog exists only when it has something to
      // add — more than one order, or one the counter accepted after the round
      // had opened.
      markRoundReady: { one: 'Mark {count} order ready', other: 'Mark {count} orders ready' },
      // The visible label, then the round it belongs to: several of these sit on
      // one page, and the time is what tells them apart. It leads with the
      // visible words so the accessible name still contains the label.
      markRoundReadyAria: {
        one: 'Mark {count} order ready — round ready by {time}',
        other: 'Mark {count} orders ready — round ready by {time}',
      },
      markRoundReadyTitle: {
        one: 'Mark {count} order ready?',
        other: 'Mark {count} orders ready?',
      },
      markRoundReadyMessage:
        'Their customers are told to come to the counter. This cannot be undone.',
      markRoundReadyLateNote: {
        one: '1 order was accepted after this round started ({time}). It will be marked ready too.',
        other:
          '{count} orders were accepted after this round started. They will be marked ready too.',
      },
      markRoundReadyConfirm: { one: 'Mark {count} ready', other: 'Mark {count} ready' },
      roundReadyToast: { one: '{count} order marked ready', other: '{count} orders marked ready' },
      // ---- marking one ticket ready ----------------------------------------
      // The by-order ticket's own action. The words are the board's — one action,
      // one name — and the accessible name adds the order, because a grid of these
      // is a grid of identical buttons to anything that cannot see the cards.
      markReady: 'Mark ready',
      markReadyAria: 'Mark ready — order {number}',
      // What a mark that did not land says. One ticket names itself; a round is
      // refused whole, so there is no count to report — only the reason, and the
      // round still on the page with its own button.
      markFailedOne: 'Order {number} was not marked ready. {reason}',
      markRoundFailed: 'This round was not marked ready. {reason}',
      // ---- when to cut the next round --------------------------------------
      // Advice, not automation. The cut is still the merchant's to make: the two
      // numbers behind it are their own round window and their own median batch,
      // and the sentences say which one fired rather than only that it did.
      cutoffStartEnough:
        'Start a round now — {count} orders waiting, enough to fill one batch.',
      cutoffStartWaited: 'Start a round now — the oldest order has waited {waited}.',
      cutoffHold: 'Hold {waited} more — {count} waiting, still filling the round.',
      cutoffBusy: 'The last hour is running at {percent}% of what this kitchen can clear.',
    },

    // ---- capacity ---------------------------------------------------------
    // The kitchen measured against its own queue. Everything measured comes
    // from logged batches, so an empty log says so rather than showing zeros as
    // though they were readings.
    capacity: {
      title: 'Kitchen capacity',
      subtitle: 'What this kitchen can clear, worked out from the rounds it has cooked.',

      logTitle: 'What the log says',
      logHint: 'Every real batch, over the last {days} days.',
      logEmpty:
        'No batches logged yet. Mark a round ready on the To cook page and the measurement starts here.',
      logLearning:
        'Only {count} batches so far — enough to look at, not enough to trust. These sharpen as you cook.',
      logSummary: 'Batches {batches} · orders {orders} · typically {size} a round.',
      batchWait: 'Holding the batch',
      batchWaitHint: 'waiting for the wok to fill',
      capacityWait: 'Waiting for capacity',
      capacityWaitHint: 'nothing could have started sooner',
      cook: 'Cooking',
      // The median is the typical basket; the mean is what the queueing formula
      // needs. Both are here so the figure the calculator seeds from can be found
      // on the page it came from, rather than looking like a disagreement.
      cookHint: 'heat applied · average {mean}',

      arrivalsTitle: 'When the orders come',
      arrivalsHint: 'Orders an hour, averaged over the days that hour had any.',
      arrivalsPeak: 'Your busiest hour is {hour}:00, at about {count} orders.',

      calcTitle: 'Can this kitchen keep up?',
      calcHint: 'Nothing here is saved. Change a number to see what it would take.',
      ordersAtOnce: 'Orders at a time',
      ordersAtOnceHint: 'How many orders the kitchen can work on at once.',
      cookMinutes: 'Minutes per order (mins)',
      cookMinutesHint: 'How long one order keeps a slot busy, from starting it to it being ready.',
      arrivals: 'Orders arriving an hour',
      arrivalsRateHint: 'Your busiest hour runs about {count}.',
      clearable: 'Clears an hour',
      clearableHint: 'orders at a time × 60 ÷ minutes each',
      load: 'Loaded',
      loadHint: 'of what it can clear',
      wait: 'Wait before the heat',
      waitHint: 'At this rate, on average.',
      // The one thing the calculator cannot do, said plainly. Without it a
      // merchant setting "orders at a time" to their three woks reads "clears 23
      // an hour" as a fact about woks rather than about the kitchen as a whole.
      singlePool:
        'The kitchen is counted as one here. Separate stations — a wok, a steamer, a fryer — are not modelled, so this cannot say which one is the constraint.',
      cannotKeepUp: 'Cannot keep up',
      cannotKeepUpHint: 'Orders arrive faster than this kitchen can clear them.',
      verdictCalm: 'Comfortable. Most orders start cooking almost at once.',
      verdictBusy: 'Busy. The queue is doing real work and a surge will be felt.',
      verdictCritical: 'Nearly saturated. A small surge becomes a long wait.',
      verdictOver: 'Over capacity. From here the queue only grows.',

      windowTitle: 'The round window',
      windowHint: 'How long a round stays open for more orders to join it.',
      windowCovers: 'One round covers (mins)',
      windowBatch: 'Orders a round',
      windowBatchHint: 'At this arrival rate.',
      windowAdds: 'Holding adds',
      windowAddsHint: 'About half the window, on average.',
      windowTradeoff:
        'Shorter means less waiting and smaller batches; longer fills the wok but makes the first order wait.',
      windowSetting: 'Set this in Settings.',
    },

    orders: {
      noShows: '{count} not collected',
      collected: '{count} collected',
      // ---- the board's two axes -------------------------------------------
      // Tabs are status and only status; the bands are the deadline. Anything
      // that can be true of an order in two tabs is a chip or a band, never a
      // fourth tab.
      tabAll: 'All',
      tabAccept: 'To accept',
      tabCooking: 'Cooking',
      tabReady: 'Ready',
      lateCount: { one: '{count} late', other: '{count} late' },
      readyCount: { one: '{count} ready', other: '{count} ready' },
      liveCount: '{count} live',
      todayTitle: 'Today',
      todaySubtitle: {
        one: '{count} order since midnight',
        other: '{count} orders since midnight',
      },
      // The day's record, which is closed orders only: an empty one means nobody
      // has been handed anything today, not that the filter is hiding something.
      todayEmpty: 'Nothing collected today',
      todayEmptyHint: 'An order appears here once it is collected or cancelled.',
      todayNoMatch: 'No orders match today',
      todayNoMatchHint: 'Try a different code, name, or number.',
      // Shown when the day is longer than the page, so "load more" says how much
      // of it is already on screen rather than being an open-ended button.
      todayShown: '{shown} of {total}',
      todayLoadMore: 'Load more',
      backToBoard: 'Back to the board',
      bandLate: 'Late',
      bandLateRule: 'past the moment it had to be started, cooked or chased',
      bandLateReadyRule: 'on the counter too long — call them',
      bandNow: 'Now',
      bandNowRule: 'act now, or due within ten minutes',
      bandReadyNowRule: 'the chase window is close — get ready to call',
      bandReadyNextRule: 'on the counter, with time to spare',
      bandNext: 'Next',
      bandNextRule: 'due within the hour',
      // The board's sort control. The trigger shows the ordering in force and the
      // menu lists all three, so each label names a state rather than an action.
      sortLabel: 'Sort the board',
      sortHint: 'Changes the order of the cards inside each band.',
      sortUrgency: 'Urgency',
      sortOldest: 'Oldest first',
      sortNewest: 'Newest first',
      shelfBooked: '{count} more booked today',
      shelfNext: 'next at {next}, last at {last}',
      shelfNothing: 'Nothing due for {duration}',
      bookedAt: 'Booked {when}',
      readyBy: 'Ready by {when}',
      placedAt: 'Placed {when}',
      bookedInsidePrepChip: 'Booked inside prep',
      stuck: 'Stuck {duration}',
      cancelWhy: 'Why are you cancelling?',
      cancelWhyHint: 'Only Didn\'t collect counts against the customer. The other two are the shop\'s own reasons and never appear in their history.',
      blockedToast: 'That number can\'t order from you again.',
      blockFailedToast: 'Cancelled, but the number could not be blocked.',
      cancelWithReason: 'Cancel order',
      reasonNoShow: "Didn't show up",
      reasonSoldOut: 'Sold out',
      reasonOther: 'Other',
      blockNumber: 'Block this number',
      blockedNote: 'This number won\'t be able to order from you. You can unblock it in Settings.',
      fromElsewhere: 'Ordered from {country}',
      title: 'Orders',
      searchPlaceholder: 'Search code, name, or phone',
      // The till's other way in: the customer's QR names the order, so scanning
      // finds it without a word being typed. The camera sits in a sheet, and a
      // scan only *opens* the order — handing it over stays the merchant's press,
      // so the code is never spent by the act of being read.
      scanCode: 'Scan a pickup code',
      scanTitle: 'Scan a pickup code',
      scanHint:
        "Point the camera at the QR on the customer's order screen. The order it names opens here.",
      scanLooking: 'Looking up the order…',
      scanForeign: 'That is not one of our codes. Search by code, name, or phone instead.',
      scanNoMatch:
        "No order matches that code. Check the customer's screen, or search by name or phone.",
      emptyActive: 'No orders in progress',
      emptyActiveHint: 'New orders appear here the moment a customer places one.',
      newBadge: 'New',
      scheduledBadge: 'Scheduled',
      // ---- unused, and kept for the orders rework --------------------------
      // `OrderCard.timePressure()` computes the six below and nothing renders
      // them, which makes them read as leftovers to a dead-key sweep. Nine more
      // in this block have no reader at all: `newBadge`, `target`, `placed`,
      // `readyIn`, `ready`, `overdue`, `pickupAt`, `readyNow` and
      // `cancelWithReason`. The merchant orders page is being rebuilt and this is
      // the wording it will want, so they are marked rather than deleted.
      waitingTooLong: 'Waiting too long',
      notCollected: 'Not collected',
      codeLabel: 'Order code',
      // The filter strip, and the two things a chip can be pressed for.
      showStatusTab: 'Show only {status} orders',
      filterByChip: 'Filter by {chip}',
      clearChip: 'Clear the {chip} filter',
      showingChip: 'Showing: {chip}',
      clearFilter: 'Clear the filter',
      showingOfLive: { one: '{shown} of {total} live', other: '{shown} of {total} live' },
      pastEstimate: 'Past estimate',
      target: 'Target',
      placed: 'Placed',
      readyIn: 'Ready in',
      ready: 'Ready',
      overdue: 'Overdue',
      pickupAt: 'Pickup at',
      startBy: 'Start by {when}',
      bookedInsidePrep: 'Booked inside the prep time',
      bookedInsidePrepHint:
        'This was already past its start time when it arrived, so the kitchen never had a clear run at it.',
      note: 'Note: {note}',
      accept: 'Accept',
      startPreparing: 'Start preparing',
      markReady: 'Mark ready',
      handedOver: 'Handed over',
      handedOverFull: 'Handed over to customer',
      readyNow: 'Now',
      detailTitle: 'Order {number}',
      openOrder: 'Open order {number}',
      callCustomer: 'Call customer',
      cancelOrder: 'Cancel order',
      cancelTitle: 'Cancel order {number}?',
      cancelSubtitle:
        'The customer sees the cancellation immediately. Stock returns to inventory.',
      cancelWarning: 'This cannot be undone',
      cancelWarningHint:
        'If food has already been prepared, consider whether the customer should be told at the counter instead.',
      cancelReason: 'Reason (optional)',
      cancelReasonPlaceholder: 'Customer called to cancel',
      cancelReasonHint: 'Stored on the order and shown in its history.',
      cancelKeep: 'Keep order',
      cancelConfirm: 'Cancel order',
      cancelledToast: 'Order {number} cancelled',
      statusToast: 'Order {number} — {status}',
      // A run of one status inside a single toast window, collapsed into a count:
      // three hand-overs are one number rather than three bars over the board.
      // The single-order line above is what a lone action still says.
      statusRunToast: '{count} orders — {status}',
      // ---- the money, which is not a status ---------------------------------
      // Cash is taken at the counter, and when it is taken is a fact of its own: a
      // customer waiting in line can pay before the bag is packed, and a regular can
      // settle up next time. The chip is the only place the board says it, and only
      // when it is true — an unpaid order carries nothing, and the amount beside the
      // number is what gets asked for.
      paid: 'Paid',
      // The chip's other half. The board states the fact either way, because at the
      // amount "nothing said" and "not paid" are the same thing; the sheet's
      // sentence is longer because it has the room.
      unpaid: 'Unpaid',
      payment: 'Payment',
      notPaid: 'Not paid yet',
      paidAt: 'Paid at {time}',
      markPaid: 'Mark paid',
      markUnpaid: 'Mark unpaid',
      paidToast: 'Order {number} — paid',
      unpaidToast: 'Order {number} — not paid',
      timeline: 'Timeline',
      estimate: 'Estimate',
      estimateScheduled: 'Scheduled pickup, booked at {when}.',
      estimateQuoted:
        'Quoted {minutes} min using the {model} model{samples}{queue}.',
      estimateSamples: ' ({count} similar orders)',
      estimateQueue: ', with {count} ahead in the queue',
      receiptSent: 'Receipt sent to {email}.',
      receiptNone: 'No email supplied, so no receipt was sent.',
      loading: 'Loading orders…',
      stepPlaced: 'Placed',
      stepAccepted: 'Accepted',
      stepPreparing: 'Preparing',
      stepReady: 'Ready',
      stepCollected: 'Collected',
    },

    menu: {
      itemCount: '{count} items',
      itemCountOne: '1 item',
      moveTitleOne: 'Move this item to…',
      deleteItemsTitleOne: 'Delete this item?',
      deletedCount: '{count} items deleted',
      deleteItemsTitle: 'Delete {count} items?',
      deleteItemsMessage: 'They are removed from the menu and from the customer app immediately. This cannot be undone.',
      undo: 'Undo',
      undoDone: 'Put back where they were',
      select: 'Select',
      selectDone: 'Done',
      selectedCount: '{count} selected',
      moveTo: 'Move to…',
      moveTitle: 'Move {count} items to…',
      moveFrom: 'From',
      moveAlreadyHere: '{count} already here',
      moveNothingToMove: 'Nothing to move',
      moveDoneOne: 'Moved to {category}',
      moveDone: 'Moved {count} items to {category}',
      reorder: 'Reorder',
      reorderUp: 'Move {name} up',
      reorderDown: 'Move {name} down',
      extrasDuplicateTitle: 'Two choices have the same name',
      extrasDuplicate: 'The kitchen will see "{name}" twice with no way to tell them apart.',
      extrasUpToTooHigh: 'A group asks for more choices than it has.',
      extrasUpToTooManyTitle: 'More than there are choices',
      extrasUpToTooMany: 'This group has {count} choices, so the most they can pick is {count}.',
      extrasUpTo: 'Up to',
      extrasUpToHint: 'The most they may pick. Defaults to all {count}.',
      extrasAbsoluteOneOnly: 'One, because a choice that is the price cannot be added to another. For choices that add up, use the option above.',
      extrasNameRequiredTitle: 'This group has no name',
      extrasNameRequired: 'Give it a name, or its choices will not be saved.',
      extrasIntentExtras: 'No — it does not set the price',
      extrasSetsPriceConsequence: 'This dish will cost {price} — its cheapest choice. The price above is ignored.',
      extrasHowMany: 'How many can they pick?',
      extrasHowManyOne: 'One',
      extrasHowManyAny: 'Any',
      extrasPriceQuestion: 'Does this group change the price?',
      extrasIntentSets: 'Yes — each is the price of the dish',
      priceFromGroup: 'This dish is priced by its options below.',
      extrasAllFreeTitle: 'Every choice in this group is free',
      extrasAllFree: 'These choices set the price, so this dish would cost nothing. For choices that do not change the price — sugar, ice, spice — turn "These set the price" off.',
      extrasTitle: 'Extras',
      extrasHint: 'Choices on the dish itself — sugar and ice, or toppings.',
      extrasName: 'Group name',
      extrasNamePlaceholder: 'Toppings',
      extrasSetsPrice: 'These set the price',
      extrasSetsPriceHint: 'For a size: the choice is what the dish costs, not an extra.',
      extrasRequired: 'The customer must choose',
      extrasChoiceName: 'Choice',
      extrasChoicePrice: 'Price ({currency})',
      extrasAdd: 'Add a group',
      extrasAddChoice: 'Add a choice',
      extrasRemove: 'Remove group',
      extrasRemoveChoice: 'Remove choice',
      scanLimited: 'That is all {limit} scans for today. More tomorrow, or add dishes by hand.',
      scanAdd: 'Add {count} to the menu',
      scanAddNone: 'Nothing selected',
      scanAdding: 'Adding {done} of {total}…',
      scanAdded: '{count} added to your menu',
      scanExists: 'Already on your menu',
      scanNoPrice: 'No readable price — add it by hand',
      scanUnreachable: 'The scan service is not running — start it with bun scripts/scan-api.ts.',
      scanTitle: 'Scan a menu',
      scanSubtitle: 'Photograph a printed menu and review what is read.',
      scanChoose: 'Choose a photo',
      scanReading: 'Reading the menu…',
      scanFound: '{categories} sections, {items} items',
      scanFailed: 'That photo could not be read.',
      hasOptions: 'This dish comes in options',
      hasOptionsHint: 'Turn this on to price the dish by size, kind, or filling. The price above is then set by the options.',
      priceFromOptions: 'Set by the options below.',
      optionsRequired: 'Add at least one option, with a name and a price.',
      optionPriceRequired: 'Every option needs a price.',
      minQuantity: 'Smallest order',
      minQuantityHint: 'Leave at 1 for no minimum. Set 5 for a dish sold in fives — the customer starts at 5 and cannot go below it.',
      options: 'Options',
      optionsHint: 'Optional. Give this dish choices — small or large, chicken or pork, half or whole — each at its own price. Leave the list empty and it is sold whole at the price above.',
      optionName: 'Option',
      optionNamePlaceholder: 'Small',
      optionPrice: 'Price ({currency})',
      removeOption: 'Remove {name}',
      remove: 'Remove',
      addOption: 'Add an option',
      title: 'Menu',
      hiddenCount: '{count} hidden',
      newCategory: 'New category',
      newItem: 'New item',
      uncategorised: 'Uncategorised',
      noPhoto: 'No photo',
      hidden: 'Hidden',
      scheduledBadge: 'Scheduled',
      left: '{count} left',
      offPercent: '{value}% off',
      offAmount: '{value} off',
      stock: 'Stock',
      actionsFor: 'Actions for {name}',
      hideFromCustomers: 'Hide from customers',
      showToCustomers: 'Show to customers',
      empty: 'Nothing here yet',
      emptyHint: 'Add your first dish and it will appear on the customer menu immediately.',
      emptyNoCategories: 'No categories yet',
      emptyNoCategoriesHint:
        'Dishes live inside a category. Make one and the menu can start filling up.',
      loading: 'Loading your menu…',
      noRestaurant: 'No restaurant selected',
      categoryNew: 'New category',
      categoryRename: 'Rename category',
      categoryName: 'Name',
      categoryNamePlaceholder: 'Mains',
      categoryDescriptionPlaceholder: 'Rice and noodles, cooked to order',
      categoryHint:
        'Categories group dishes on the customer menu. Deleting one keeps its dishes and moves them to Uncategorised.',
      deleteItemTitle: 'Delete {name}?',
      deleteItemMessage:
        'Past orders keep their own record of this dish, so sales history is unaffected. This cannot be undone.',
      deleteCategoryMessage:
        'The dishes in this category are kept and moved to Uncategorised. This cannot be undone.',
      addItemToCategory: 'Add item',
      addToMenu: 'Add to menu',
      stockTitle: 'Stock for {name}',
      stockSubtitle: 'The dish is hidden from customers automatically at zero.',
      stockRemaining: 'Remaining',
      saved: '{name} saved',
      categorySaved: 'Category saved',
      deleted: '{name} deleted',
      nowOnMenu: '{name} is now on the menu',
      nowHidden: '{name} hidden',
      stockSet: '{name}: {count} in stock',
      tabDetails: 'Details',
      tabAvailability: 'Serving times',
      tabOffers: 'Offers',
      nameLabel: 'Name',
      namePlaceholder: 'Nyonya Laksa',
      descriptionPlaceholder: 'What is in it, and how is it served?',
      priceLabel: 'Price ({currency})',
      pricePlaceholder: '9.80',
      priceHint: 'Customers pay {price} before tax and offers.',
      categoryLabel: 'Category',
      prepLabel: 'Prep time (minutes)',
      prepHint:
        'Time this dish adds to an order. Set 0 to use the restaurant default of {minutes} minutes.',
      visibleLabel: 'Visible on the menu',
      visibleHint: 'Hide a dish without deleting it — useful for seasonal items.',
      trackStockLabel: 'Track stock',
      trackStockHint:
        'Hides the dish automatically when the count reaches zero, and returns stock if an order is cancelled.',
      inStockLabel: 'In stock now',
      replacePhoto: 'Replace photo',
      addPhoto: 'Add a photo',
      removePhoto: 'Remove photo',
      keepPhoto: 'Keep photo',
      photoHint: 'Tap the image to choose a different one.',
      servingTimesInfo: 'Serving times',
      servingTimesInfoHint:
        'With no times set, the dish is available whenever you are open. Add times to limit it to a day part — breakfast items, for example.',
      alwaysAvailable: 'Always available',
      alwaysAvailableHint: 'This dish can be ordered any time you are open.',
      addServingTime: 'Add a serving time',
      from: 'From',
      until: 'Until',
      removeWindow: 'Remove',
      addAnotherDay: 'Add another day',
      copyToEveryDay: 'Copy to every day',
      clearAll: 'Clear all',
      timesNote:
        'Times are in {timezone}. A window that ends earlier than it starts runs past midnight.',
      saveServingTimes: 'Save serving times',
      saveFirst: 'Save the dish first',
      saveFirstHint:
        'Offers are attached to a dish, so add it to the menu before scheduling a discount.',
      currentOffers: 'Current and scheduled offers',
      currentOffersHint: 'If several overlap, customers automatically get the best one.',
      noOffers: 'No offers on this dish.',
      runningNow: 'Running now',
      finished: 'Finished',
      scheduledLabel: 'Scheduled',
      scheduleOffer: 'Schedule an offer',
      offerType: 'Type',
      percentage: 'Percentage',
      amountOff: 'Amount ({currency})',
      percentOff: 'Percent off',
      amountOffLabel: 'Amount off ({currency})',
      offerLabel: 'Label',
      offerLabelPlaceholder: 'Happy hour',
      offerLabelHint: 'Shown on the customer menu.',
      starts: 'Starts',
      ends: 'Ends',
      restaurantDateTime: 'Restaurant time ({timezone})',
      customerPays: 'Customer pays during the offer',
      scheduleOfferButton: 'Schedule offer',
      offerScheduled: 'Offer scheduled',
      invalidDiscount: 'Enter a discount amount greater than zero',
      percentTooHigh: 'A percentage cannot exceed 100',
      offerEndBeforeStart: 'The offer must end after it starts',
      nameRequired: 'Give the dish a name',
      priceRequired: 'Enter a price such as 9.80',
      inventoryRequired: 'Enter how many are available',
      categoryFirstName: 'Name is required',

      // The item editor. A card heading and a one-line hint for every block,
      // which is the idiom the shadcn product-create page is the example of.
      cardDetailsHint: 'What the customer reads on the menu.',
      cardPrice: 'Price',
      cardPriceHint: 'One price for the dish.',
      cardKitchen: 'Kitchen',
      cardKitchenHint: 'How it is made, and the smallest order.',
      cardChoices: 'Choices',
      cardChoicesHint:
        'Groups of options, and how many a customer may pick from each. A group can set the price, like a size, or add to it.',
      cardAvailability: 'When it is available',
      cardAvailabilityHint: 'Leave empty to serve it all day.',
      cardOffer: 'Offer',
      cardOfferHint: 'A discount on this dish for a set time.',
      cardCategoryHint: 'One category, so every one is shown. Tapping again moves it.',
      cardPhoto: 'Photo',
      cardPhotoHint: 'Most merchants have none. It is worth the nudge.',
      onMenuLabel: 'On the menu',
      onMenuHint: 'Hidden items stay on the menu but are not shown.',
      trackLeftLabel: 'Track how many are left',
      trackLeftHint: 'Stops taking orders at zero.',
      groupPickOne: 'Pick one',
      groupPickAny: 'Pick any',
      groupPickUpTo: 'Pick up to {count}',
      groupRequired: 'required',
      groupOptional: 'optional',
      groupKindSets: 'sets the price',
      groupKindFree: 'does not change the price',
      groupKindAdds: 'adds to the price',
      groupShownRow:
        'Each choice is a version of the dish, priced on its own. Shown as a row on the menu.',
      groupShownChips: 'Shown on the menu as add-ons beside the dish.',
      groupShownAdds: "Each choice adds to the dish's price. Shown as chips.",
      groupEdit: 'Edit',
      groupDone: 'Done',
    },

    hours: {
      title: 'Opening hours',
      intro:
        'All times are {timezone}. Customers can only place or schedule orders while you are open.',
      closedEveryDay: 'You are closed every day',
      closedEveryDayHint: 'No customer will be able to order. Set at least one trading day.',
      weekly: 'Weekly hours',
      opens: 'Opens',
      closes: 'Closes',
      closesBeforeItOpens:
        'Closes before it opens. Past midnight belongs on {day}: add a window there instead.',
      closesBeforeItOpensOneOff:
        'Closes before it opens. A one-off past midnight belongs on the following date.',
      addWindow: 'Add another window',
      removeWindow: 'Remove',
      overlappingWindows: 'Two windows on {day} overlap. Shorten one of them.',
      saveHours: 'Save hours',
      unsaved: 'Unsaved changes',
      exceptions: 'Exceptions',
      exceptionsHint:
        'Public holidays and one-off changes. A date here replaces the weekly rule for that day.',
      noExceptions: 'No exceptions — your weekly hours apply every week.',
      addException: 'Add an exception',
      closedAllDay: 'Closed all day',
      closedAllDayHint: 'Turn off to open at different times instead.',
      exceptionNotePlaceholder: 'Closed for a private event',
      addExceptionButton: 'Add exception',
      removeTitle: 'Remove this exception?',
      removeMessage: 'Your regular weekly hours will apply to that date again.',
      saved: 'Opening hours saved',
      exceptionSaved: 'Exception saved',
      exceptionRemoved: 'Exception removed',
      invalidDate: 'Enter a date as YYYY-MM-DD',
      loading: 'Loading opening hours…',
      noRestaurant: 'No restaurant selected',
      hourRange: '{open} – {close}',
      overnightSuffix: ' (+1 day)',
      weekdayLong0: 'Sunday',
      weekdayLong1: 'Monday',
      weekdayLong2: 'Tuesday',
      weekdayLong3: 'Wednesday',
      weekdayLong4: 'Thursday',
      weekdayLong5: 'Friday',
      weekdayLong6: 'Saturday',
    },

    tables: {
      title: 'Tables and counters',
      intro:
        'Print a code for each place a customer might sit or wait, so orders can be attributed to where they are.',
      neverExpire: 'These codes never expire on their own',
      add: 'Add a code',
      neverExpireHint:
        'The order is tied to the restaurant either way — the code only tells the kitchen where the customer is. Disable a code if a sticker goes missing.',
      empty: 'No codes yet',
      emptyHint: 'Add your counter and each table to generate printable QR codes.',
      seats: '{count} seats',
      active: 'Active',
      disabled: 'Disabled',
      enable: 'Enable',
      disable: 'Disable',
      addTitle: 'Add a code',
      addSubtitle: 'Where will this sticker live?',
      labelField: 'Label',
      labelPlaceholder: 'Table 7',
      seatsField: 'Seats',
      seatsPlaceholder: '4',
      seatsHint: 'Optional. Leave blank for a counter or waiting area.',
      create: 'Create code',
      deleteTitle: 'Delete {label}?',
      deleteMessage:
        'The printed sticker will stop working immediately. Past orders keep their record of it.',
      added: '{label} added',
      deleted: '{label} deleted',
      enabled: '{label} re-enabled',
      disabledToast: '{label} disabled',
      printSubtitle: 'Scan to order and pay at the counter',
      printThis: 'Print this',
      copyUrl: 'Copy URL',
      copyFailed: 'Could not copy the link.',
      loading: 'Loading table codes…',
      noRestaurant: 'No restaurant selected',
    },

    settings: {
      blockedHint: "Numbers you have blocked. They can't order from you until you unblock them.",
      unblockedToast: 'That number can order again.',
      unblockFailedToast: 'Could not lift the block.',
      blockedTitle: 'Blocked numbers',
      blockedEmpty: 'No numbers are blocked.',
      blockedSince: 'Blocked {date}',
      unblock: 'Unblock',
      webAddress: 'Web address',
      webAddressHint: "Your menu link. Can't be changed here, or the links customers already have would stop working.",
      title: 'Settings',
      details: 'Details',
      detailsHint: 'What customers read about the restaurant.',
      cover: 'Cover',
      coverHint: 'Shown at the top of your menu.',
      coverImage: 'Cover image',
      restaurantName: 'Restaurant name',
      descriptionLabel: 'Description',
      phoneLabel: 'Phone',
      phoneHint: 'Customers can tap this number to call you.',
      addressLabel: 'Address',
      addressHint: 'Where customers pick up their order.',
      currencyHint:
        'Prices on your menu and in reports use this currency, which the restaurant was created with. It cannot be changed here, because every price would show in the new currency without being converted.',
      paused: 'Ordering is paused',
      pausedHint:
        'Customers can browse your menu but cannot place an order. Turn this back on when the kitchen is ready.',
      acceptOrders: 'Accept orders',
      autoAccept: 'Start cooking immediately',
      autoAcceptHint:
        'New orders go straight to the kitchen instead of waiting for you to accept them. Scheduled orders still wait for you to accept them.',
      acceptOrdersHint:
        'Turn off to pause ordering during a rush or a stock-take. Existing orders are unaffected.',
      minNotice: 'Minimum notice',
      minNoticeHint: 'Earliest a customer can book a pickup.',
      bookAhead: 'Book up to',
      slotSpacing: 'Slot spacing',
      slotSpacingHint: 'How far apart pickup times are offered.',
      timezone: 'Time zone (region)',
      timezoneHint:
        'Pickup times follow this zone. It cannot be changed here, because it would change the times of pickups customers have already booked.',
      pickupTimes: 'Pickup times',
      pickupTimesHint: 'These decide which pickup times customers can choose.',
      money: 'Money',
      moneyHint: 'The currency on your menu and the tax added at checkout.',
      taxRate: 'Tax rate',
      taxRateHint: 'Applied to the discounted subtotal.',
      taxExample:
        'On a {price} dish, customers pay {total}. Set 0 if your prices already include tax.',
      prepEstimates: 'Prep time estimates',
      prepEstimatesHint:
        "Used until we've learned your real prep times from completed orders.",
      learnedFrom: 'Based on {count} completed orders',
      stillLearning: 'Still learning',
      medianPrep: 'Typical {duration}',
      overhead: 'Extra time per order',
      overheadHint: 'Added to every order for plating, bagging and queueing.',
      fastest: 'Fastest possible',
      fastestHint: 'No estimate is ever shorter than this.',
      slowest: 'Slowest possible',
      slowestHint:
        'The ceiling on every estimate. An order that needs longer is still quoted {minutes} min.',
      capBindsTitle: 'Every estimate is capped at {cap} min',
      // Deliberately quotes no figure from the samples. `prep_seconds` is
      // placed-at to ready-at, so one batch of old orders marked ready together
      // turns the percentile into hours — and the sentence is about the ceiling,
      // which is the part the merchant can act on. The card's own "Typical"
      // badge carries the measured number.
      capBindsHint:
        'Your slowest completed orders take longer than {cap} min, so the model cannot report them honestly — and the checkout never flags a pickup time the kitchen cannot make.',
      tellingTitle: 'Orders waiting to be accepted',
      tellingHint:
        'When a customer is told nobody has accepted their order yet — and when the board calls an unaccepted order late.',
      acceptGrace: 'Show the waiting message after',
      minutesUnit: 'min',
      daysUnit: 'days',
      percentUnit: '%',
      acceptGraceBasis: 'Counted from',
      acceptGracePlaced: 'Order placed',
      acceptGraceEstimate: 'Estimate',
      acceptGraceNewOrders: 'New orders',
      acceptGraceScheduledOrders: 'Scheduled orders',
      acceptGraceOutcomePlaced: 'Message {minutes} min after the order is placed.',
      acceptGraceOutcomeEstimate: 'Message {minutes} min after the estimate runs out.',
      acceptGraceOutcomeScheduled: 'Message {minutes} min after the start-by time.',
      chaseTitle: 'Waiting at the counter',
      chaseHint:
        'A ready order is waiting on the customer, not on the kitchen. After this long the board calls it late and asks the counter to call them.',
      chaseWindow: 'Call the customer after',
      chaseOutcome: 'Ready orders are marked late {minutes} min after they come off the pass.',
      cookRoundTitle: 'Cooking rounds',
      cookRoundHint:
        'How much food the To cook page asks the kitchen to start together. A round opens at the earliest ready-by in the queue and closes this long after it.',
      cookRoundWindow: 'One round covers',
      cookRoundOutcome: 'Dishes ready within {minutes} min of each other are cooked as one batch.',
      ordersAtOnce: 'Orders at a time',
      ordersAtOnceHint:
        'How many orders the kitchen can work on at once. More orders waiting means a longer estimate.',
      perDishInfo: 'How dish times are added',
      perDishInfoHint:
        "Each dish has its own prep time in the menu editor. We add your extra time, then the longest dish's prep time in full, then a share of the time for the other dishes.",
      receipts: 'Customer receipts',
      receiptsHint:
        'Emailed automatically when a customer supplies an address at checkout.',
      emailActive: 'Email delivery is active',
      emailNotConfigured: 'Email delivery is not configured',
      sentCount: '{count} sent',
      receiptsQueued: 'Receipts are queued, not sent',
      receiptsQueuedHint:
        '{count} receipt(s) are waiting. Add a transactional email key to start delivering them — the contents are already rendered and stored.',
      receiptsFailed: '{count} receipt(s) could not be delivered',
      receiptsFailedHint: 'Check the sending address and API key.',
      lastReceipt: 'Last receipt sent {when}.',
      noReceiptsYet: 'No receipts sent yet.',
      checkingEmail: 'Checking email delivery…',
      verificationTitle: 'Verification',
      verificationHint:
        "Yummy checks a restaurant's name, address and contact details before marking it.",
      verificationSince: 'Verified since {when}.',
      sessions: 'Sessions',
      sessionsHint: 'Signs out all devices, including this one.',
      signOutEverywhere: 'Sign out everywhere',
      signOutEverywhereTitle: 'Sign out everywhere?',
      signOutEverywhereMessage:
        'All devices are signed out, including this one. Other devices will be signed out within the hour.',
      signOutEverywhereFailed: 'Could not sign out everywhere. Check the connection and try again.',
      discard: 'Discard',
      save: 'Save settings',
      saved: 'Settings saved',
      loading: 'Loading settings…',
      noRestaurant: 'No restaurant selected',
      loadFailed: 'Could not load settings',
    },

    appearance: {
      light: 'Light',
      dark: 'Dark',
      system: 'System',
    },
  },
} as const;

/**
 * Widens every leaf to `string` while preserving the tree structure.
 *
 * Without this, `as const` would force other locales to reuse the English
 * literals verbatim.
 */
type WideLeaves<T> = {
  [K in keyof T]: T[K] extends string
    ? string
    : T[K] extends { one: string; other: string }
      ? { one: string; other: string }
      : WideLeaves<T[K]>;
};

export type Messages = WideLeaves<typeof en>;

/** Every dot path that resolves to a translatable leaf. */
export type MessageKey = LeafPaths<Messages>;

type LeafPaths<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : T[K] extends { one: string; other: string }
      ? `${Prefix}${K}`
      : LeafPaths<T[K], `${Prefix}${K}.`>;
}[keyof T & string];
